// src/components/LinxDispatch.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PrintIcon from '@mui/icons-material/Print';
import axios from 'axios';
import Layout from './Layout';
import SearchForm from './SearchForm';
import OrderDetails from './OrderDetails';
import OrderItems from './OrderItems';
import ConfirmationDialog from './ConfirmationDialog';
import PrintPackingSlip from './PrintPackingSlip';
import { alpha } from '@mui/material/styles';
import {
  buildMintsoftDisplayGroups,
  getMintsoftBundleProductIds,
  isMintsoftDisplayGroupConfirmed,
} from '../utils/mintsoftBundles';

const MINTSOFT_QUERY_RAISED_STATUS_ID = 13;
const SHIPMENT_DRAFT_STORAGE_KEY = 'linxDispatchShipmentDrafts';
const MINTSOFT_DISPATCH_LOCK_STORAGE_KEY = 'linxDispatchActiveDispatches';
const MINTSOFT_DISPATCH_LOCK_TTL_MS = 10 * 60 * 1000;
const MINTSOFT_PRODUCT_LOOKUP_TIMEOUT_MS = 8000;
const MINTSOFT_ORDER_TIMEOUT_MS = 60000;
const MINTSOFT_DISPATCH_REQUEST_TIMEOUT_MS = 210000;
const KORNITX_READY_STATUS = 'In Production';
const KORNITX_MISSING_STATUSES = ['Not Found'];

const isKornitxMissingStatus = (status) => KORNITX_MISSING_STATUSES.includes(status);

const getMintsoftDispatchBlockedMessage = (mintsoftOrder) => {
  if (mintsoftOrder?.OrderStatusId !== MINTSOFT_QUERY_RAISED_STATUS_ID) {
    return '';
  }

  const orderReference = mintsoftOrder.OrderNumber || mintsoftOrder.ID || 'This order';
  return `Order ${orderReference} is QUERYRAISED in Mintsoft. Resolve the query/comments before dispatching.`;
};

const readShipmentDrafts = () => {
  try {
    const stored = localStorage.getItem(SHIPMENT_DRAFT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Unable to read shipment drafts from localStorage:', error);
    return {};
  }
};

const getShipmentDraft = (orderId) => {
  if (!orderId) return null;
  const drafts = readShipmentDrafts();
  return drafts[String(orderId)] || null;
};

const saveShipmentDraft = (orderId, draft) => {
  if (!orderId) return;

  try {
    const drafts = readShipmentDrafts();
    drafts[String(orderId)] = draft;
    localStorage.setItem(SHIPMENT_DRAFT_STORAGE_KEY, JSON.stringify(drafts));
  } catch (error) {
    console.warn('Unable to save shipment draft to localStorage:', error);
  }
};

const removeShipmentDraft = (orderId) => {
  if (!orderId) return;

  try {
    const drafts = readShipmentDrafts();
    delete drafts[String(orderId)];
    localStorage.setItem(SHIPMENT_DRAFT_STORAGE_KEY, JSON.stringify(drafts));
  } catch (error) {
    console.warn('Unable to remove shipment draft from localStorage:', error);
  }
};

const readMintsoftDispatchLocks = () => {
  try {
    const stored = localStorage.getItem(MINTSOFT_DISPATCH_LOCK_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Unable to read active dispatches from localStorage:', error);
    return {};
  }
};

const writeMintsoftDispatchLocks = (locks) => {
  try {
    localStorage.setItem(MINTSOFT_DISPATCH_LOCK_STORAGE_KEY, JSON.stringify(locks));
  } catch (error) {
    console.warn('Unable to save active dispatches to localStorage:', error);
  }
};

const pruneMintsoftDispatchLocks = (locks) => {
  const now = Date.now();
  return Object.fromEntries(
    Object.entries(locks).filter(([, lock]) => (
      lock?.startedAt && now - lock.startedAt < MINTSOFT_DISPATCH_LOCK_TTL_MS
    ))
  );
};

const getActiveMintsoftDispatchLock = (orderId) => {
  if (!orderId) return null;
  const locks = pruneMintsoftDispatchLocks(readMintsoftDispatchLocks());
  writeMintsoftDispatchLocks(locks);
  return locks[String(orderId)] || null;
};

const saveMintsoftDispatchLock = (orderId, lock) => {
  if (!orderId) return;
  const locks = pruneMintsoftDispatchLocks(readMintsoftDispatchLocks());
  locks[String(orderId)] = lock;
  writeMintsoftDispatchLocks(locks);
};

const removeMintsoftDispatchLock = (orderId, token) => {
  if (!orderId) return;
  const locks = pruneMintsoftDispatchLocks(readMintsoftDispatchLocks());
  const lock = locks[String(orderId)];
  if (!lock || (token && lock.token !== token)) return;
  delete locks[String(orderId)];
  writeMintsoftDispatchLocks(locks);
};

const normalizeShipmentValue = (value) => String(value ?? '').trim();
const isNumericOrderId = (value) => /^\d+$/.test(String(value ?? '').trim());
const isMintsoftProductPersonalised = (product) => (
  product?.ProductInCategories?.some((cat) => cat?.ProductCategory?.Name === 'Personalised')
);

const LinxDispatch = () => {
  // activeTab: "dispatch" for Mintsoft, "kornitx" for KornitX, "reprint" for reprint.
  const [activeTab, setActiveTab] = useState('dispatch');
  const [searchParams, setSearchParams] = useState({
    orderNumber: '',
    searchType: 'orderId',
    selectedClientId: '',
  });
  const [order, setOrder] = useState(null); // holds either Mintsoft or KornitX order data
  const [orderProducts, setOrderProducts] = useState({}); // only for Mintsoft orders
  const [mintsoftProductLookupComplete, setMintsoftProductLookupComplete] = useState(false);
  const [mintsoftProductLookupFailedIds, setMintsoftProductLookupFailedIds] = useState([]);
  const [bundleDetails, setBundleDetails] = useState({}); // only for grouped Mintsoft bundle display
  const [lineImagesMap, setLineImagesMap] = useState({}); // only for Mintsoft personalised images
  const [loading, setLoading] = useState(false);
  const [printWorkflow, setPrintWorkflow] = useState({
    active: false,
    stage: 'idle',
    message: '',
    severity: 'info',
    additionalDocCount: 0,
    canRetryAdditional: false,
    retryMode: null,
  });
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [printerProxyUnavailable, setPrinterProxyUnavailable] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, orderToReprint: null });
  const [kornitxBypassDialog, setKornitxBypassDialog] = useState({ open: false, status: '' });
  const [courierServices, setCourierServices] = useState([]);
  const [selectedCourierService, setSelectedCourierService] = useState('');
  const [numberOfParcels, setNumberOfParcels] = useState('');
  const [isEditingShipment, setIsEditingShipment] = useState(false);
  const orderInputRef = useRef(null);
  const shipmentSnapshotRef = useRef({
    selectedCourierService: '',
    numberOfParcels: '',
  });
  const [kornitxOrderStatus, setKornitxOrderStatus] = useState('');
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [confirmedLineIds, setConfirmedLineIds] = useState([]);
  const addAllButtonRef = useRef(null);
  const dispatchButtonRef = useRef(null);
  const mintsoftDispatchInFlightRef = useRef(false);
  const [giftWidth, setGiftWidth] = useState('102mm');
  const [giftHeight, setGiftHeight] = useState('152mm');
  const presetSizes = {
    '4x6" (102 x 152 mm)': { width: '102mm', height: '152mm' },
    'A6 (105 x 148 mm)': { width: '105mm', height: '148mm' },
    'Custom': null,
  };
  const [selectedSize, setSelectedSize] = useState('Custom');

  // NEW: State for KornitX Shipping Details
  const [kxShippingCarrier, setKxShippingCarrier] = useState('');
  const [kxShippingMethod, setKxShippingMethod] = useState('');
  const [isEditingKXShipping, setIsEditingKXShipping] = useState(false);


  useEffect(() => {
    const savedWidth = localStorage.getItem('giftWidth');
    const savedHeight = localStorage.getItem('giftHeight');
    if (savedWidth && savedHeight) {
      setGiftWidth(savedWidth);
      setGiftHeight(savedHeight);
      setSelectedSize('Custom');
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('giftWidth', giftWidth);
    localStorage.setItem('giftHeight', giftHeight);
  }, [giftWidth, giftHeight]);


  const mintsoftOrderItems = activeTab === 'dispatch' && Array.isArray(order?.OrderItems)
    ? order.OrderItems
    : [];
  const mintsoftDisplayGroups = buildMintsoftDisplayGroups(mintsoftOrderItems);
  const mintsoftProductsReady = mintsoftOrderItems.length > 0 && mintsoftProductLookupComplete;
  const canTrustMintsoftPersonalisationHint = mintsoftProductsReady
    && mintsoftProductLookupFailedIds.length === 0;
  const personalisedMintsoftItemIds = mintsoftProductsReady
    ? mintsoftOrderItems
      .filter((item) => isMintsoftProductPersonalised(orderProducts[item.ProductId]))
      .map((item) => item.ID)
    : [];
  const hasPersonalisedMintsoftItems = personalisedMintsoftItemIds.length > 0;
  const isMintsoftProductLookupPending = activeTab === 'dispatch'
    && mintsoftOrderItems.length > 0
    && !mintsoftProductLookupComplete;

  // For Mintsoft orders, we use product details and personalised images.
  // For KornitX orders, the data structure is different.
  // Pass a prop "orderSource" with value "mintsoft" or "kornitx" to reused components.
  const isPersonalised = (itemId) => personalisedMintsoftItemIds.includes(itemId) || Boolean(lineImagesMap[itemId]);

  // ------------------------------
  // Fetch printers on mount
  // ------------------------------
  useEffect(() => {
    axios
      .get('http://localhost:5237/printers')
      .then((response) => {
        const printerList = response.data;
        setPrinterProxyUnavailable(false);
        setPrinters(printerList);
        const zDesignerPrinter = printerList.find((printer) =>
          printer.startsWith('ZDesigner')
        );
        if (zDesignerPrinter) {
          setSelectedPrinter(zDesignerPrinter);
        } else if (printerList.length > 0) {
          setSelectedPrinter(printerList[0]);
        }
      })
      .catch((error) => {
        if (error?.code === 'ERR_NETWORK' && !error?.response) {
          setPrinterProxyUnavailable(true);
          setPrinters([]);
          setSelectedPrinter('');
          return;
        }

        console.error('Error fetching printers:', error);
      });
  }, []);

  // ------------------------------
  // Fetch courier services on mount
  // ------------------------------
  useEffect(() => {
    axios
      .get('api/linx-dispatch/courier-services', { timeout: MINTSOFT_ORDER_TIMEOUT_MS })
      .then((response) => {
        setCourierServices(response.data);
      })
      .catch((error) => {
        console.error('Error fetching courier services:', error);
      });
  }, []);

  // ------------------------------
  // Focus search input on tab change
  // ------------------------------
  useEffect(() => {
    if (orderInputRef.current) {
      orderInputRef.current.focus();
    }
  }, [activeTab]);

  // ------------------------------
  // For Mintsoft orders: set default numberOfParcels when order loads
  // ------------------------------
  useEffect(() => {
    if (order && activeTab === 'dispatch') {
      const savedShipmentDraft = getShipmentDraft(order.ID);
      const initialCourierService = savedShipmentDraft?.selectedCourierService ?? order.CourierServiceId ?? '';
      const initialNumberOfParcels = savedShipmentDraft?.numberOfParcels ?? order.NumberOfParcels ?? '';

      setSelectedCourierService(initialCourierService);
      setNumberOfParcels(initialNumberOfParcels);
      setIsEditingShipment(false);
      shipmentSnapshotRef.current = {
        selectedCourierService: initialCourierService,
        numberOfParcels: initialNumberOfParcels,
      };
    }
  }, [order, activeTab]);

  // ------------------------------
  // For Mintsoft orders: fetch product details
  // ------------------------------
  useEffect(() => {
    if (order && activeTab === 'dispatch' && order.OrderItems) {
      let isCurrentOrder = true;
      const fetchMintsoftProducts = async () => {
        const productMap = {};
        const failedProductIds = [];
        const uniqueProductIds = Array.from(
          new Set(order.OrderItems.map((item) => item.ProductId).filter(Boolean))
        );

        if (uniqueProductIds.length === 0) {
          if (isCurrentOrder) {
            setOrderProducts({});
            setMintsoftProductLookupFailedIds([]);
            setMintsoftProductLookupComplete(true);
          }
          return;
        }

        await Promise.all(
          uniqueProductIds.map(async (productId) => {
            try {
              const response = await axios.get(
                `/api/linx-dispatch/product/${productId}`,
                { timeout: MINTSOFT_PRODUCT_LOOKUP_TIMEOUT_MS }
              );
              productMap[productId] = response.data.product;
            } catch (err) {
              console.error(`Error fetching product ${productId}:`, err.message);
              failedProductIds.push(productId);
            }
          })
        );
        if (isCurrentOrder) {
          setOrderProducts(productMap);
          setMintsoftProductLookupFailedIds(failedProductIds);
          setMintsoftProductLookupComplete(true);
        }
      };
      setOrderProducts({});
      setMintsoftProductLookupFailedIds([]);
      setMintsoftProductLookupComplete(false);
      setLineImagesMap({});
      setKornitxOrderStatus('');
      fetchMintsoftProducts();
      return () => {
        isCurrentOrder = false;
      };
    }

    setOrderProducts({});
    setMintsoftProductLookupFailedIds([]);
    setMintsoftProductLookupComplete(false);
    setBundleDetails({});
    setLineImagesMap({});
    setKornitxOrderStatus('');
    return undefined;
  }, [order, activeTab]);

  // ------------------------------
  // For Mintsoft orders: fetch bundle display details
  // ------------------------------
  useEffect(() => {
    if (order && activeTab === 'dispatch' && order.OrderItems) {
      let isCurrentOrder = true;
      const bundleProductIds = getMintsoftBundleProductIds(order.OrderItems);

      if (!bundleProductIds.length) {
        setBundleDetails({});
        return undefined;
      }

      const fetchMintsoftBundles = async () => {
        const bundleMap = {};
        await Promise.all(
          bundleProductIds.map(async (bundleProductId) => {
            try {
              const response = await axios.get(`/api/linx-dispatch/product/${bundleProductId}/bundle`, {
                timeout: MINTSOFT_PRODUCT_LOOKUP_TIMEOUT_MS,
              });
              bundleMap[bundleProductId] = {
                product: response.data.product,
                bundle: response.data.bundle,
              };
            } catch (err) {
              console.error(`Error fetching bundle ${bundleProductId}:`, err.message);
            }
          })
        );

        if (isCurrentOrder) {
          setBundleDetails(bundleMap);
        }
      };

      setBundleDetails({});
      fetchMintsoftBundles();

      return () => {
        isCurrentOrder = false;
      };
    }

    setBundleDetails({});
    return undefined;
  }, [order, activeTab]);

  // ------------------------------
  // For Mintsoft orders: fetch personalised images
  // ------------------------------
  const fetchPersonalisedLineImages = async () => {
    if (!order?.ID || !mintsoftProductsReady) return;
    try {
      if (!hasPersonalisedMintsoftItems) {
        console.log('No personalised items. Skipping KornitX fetch.');
        setLineImagesMap({});
        return;
      }
      const personalisedRes = await axios.get(`/api/linx-dispatch/personalised-images/${order.ID}`);
      if (personalisedRes.data && Object.keys(personalisedRes.data.personalisedImages).length > 0) {
        setLineImagesMap(personalisedRes.data.personalisedImages);
      } else {
        console.log('KornitX returned no images.');
        setLineImagesMap({});
      }
    } catch (error) {
      console.error('Error fetching personalised images:', error);
      setLineImagesMap({});
    }
  };

  useEffect(() => {
    if (order && activeTab === 'dispatch' && mintsoftProductsReady) {
      fetchPersonalisedLineImages();
    }
  }, [order, activeTab, mintsoftProductsReady, hasPersonalisedMintsoftItems]);

  // ------------------------------
  // For Mintsoft orders: fetch KornitX status
  // ------------------------------
  const fetchKornitxStatus = async () => {
    if (!order?.ID || !mintsoftProductsReady) return;
    if (!hasPersonalisedMintsoftItems) {
      setKornitxOrderStatus('Not personalised');
      setCheckingStatus(false);
      return;
    }

    setCheckingStatus(true);
    try {
      const response = await axios.get(`/api/linx-dispatch/kornitx-status/${order.ID}`);
      setKornitxOrderStatus(response.data.status_name || 'Unknown');
    } catch (error) {
      console.error('Error fetching KornitX status:', error);
      setKornitxOrderStatus('Unknown');
    } finally {
      setCheckingStatus(false);
    }
  };

  useEffect(() => {
    if (order && activeTab === 'dispatch' && mintsoftProductsReady) {
      fetchKornitxStatus();
    }
  }, [order, activeTab, mintsoftProductsReady, hasPersonalisedMintsoftItems]);


  useEffect(() => {
    if (activeTab === 'kornitx') {
      setSearchParams((prev) => ({
        ...prev,
        searchType: 'orderNumber',
        selectedClientId: '',
      }));
    } else if (activeTab === 'dispatch' || activeTab === 'reprint') {
      setSearchParams((prev) => ({
        ...prev,
        searchType: 'orderId',
        selectedClientId: prev.selectedClientId || '', // Keep existing or reset
      }));
    }
  }, [activeTab]);


  // ------------------------------
  // Effect to auto-focus Dispatch Button when all items are confirmed
  // (For Mintsoft orders)
  // ------------------------------
  useEffect(() => {
    if (order && activeTab === 'dispatch' && order.OrderItems) {
      const nonPersonalisedIds = order.OrderItems
        .filter((item) => !isPersonalised(item.ID))
        .map((item) => item.ID);
      const allConfirmed = nonPersonalisedIds.every((id) => confirmedLineIds.includes(id));
      if (allConfirmed && !isEditingShipment && dispatchButtonRef.current) {
        dispatchButtonRef.current.focus();
        dispatchButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [confirmedLineIds, order, activeTab, isEditingShipment]);

  // ------------------------------
  // Effect to auto-focus Dispatch Button when all KornitX items are confirmed
  // ------------------------------
  useEffect(() => {
    if (order && activeTab === 'kornitx' && order.items && order.items.items) {
      const allItemIds = order.items.items.map((item) => item.id);
      const allConfirmed = allItemIds.every((id) => confirmedLineIds.includes(id));
      if (allConfirmed && dispatchButtonRef.current) {
        dispatchButtonRef.current.focus();
        dispatchButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [confirmedLineIds, order, activeTab]);

  // When a KornitX order is loaded, initialize the shipping details state.
  useEffect(() => {
    if (order && activeTab === 'kornitx') {
      setKxShippingCarrier(order.shipping_carrier || '');
      setKxShippingMethod(order.shipping_method || '');
    }
  }, [order, activeTab]);

  // ------------------------------
  // Notification functions
  // ------------------------------
  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };

  const handleNotificationClose = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const getTotalOrderQuantity = (mintsoftOrder = order) =>
    mintsoftOrder?.OrderItems?.reduce((sum, item) => sum + (item.Quantity || 0), 0) || 0;

  const getOriginalNumberOfParcels = (mintsoftOrder = order) =>
    String(mintsoftOrder?.NumberOfParcels ?? '').trim();

  const getEffectiveNumberOfParcels = (
    parcelsValue,
    mintsoftOrder = order,
    { fallbackToOrderValue = true } = {}
  ) => {
    const trimmedParcels = String(parcelsValue ?? '').trim();

    if (trimmedParcels) {
      return trimmedParcels;
    }

    return fallbackToOrderValue ? getOriginalNumberOfParcels(mintsoftOrder) : '';
  };

  const validateNumberOfParcels = (
    parcelsValue,
    mintsoftOrder = order,
    { fallbackToOrderValue = true } = {}
  ) => {
    if (!mintsoftOrder?.OrderItems) return null;

    const effectiveParcels = getEffectiveNumberOfParcels(parcelsValue, mintsoftOrder, {
      fallbackToOrderValue,
    });

    if (!effectiveParcels) {
      return fallbackToOrderValue ? null : 'Please enter the number of parcels.';
    }

    const parsedParcels = Number(effectiveParcels);
    if (!Number.isInteger(parsedParcels) || parsedParcels < 1) {
      return 'Number of Parcels must be a whole number greater than 0.';
    }

    const totalQty = getTotalOrderQuantity(mintsoftOrder);
    if (parsedParcels > totalQty) {
      return `Number of Parcels cannot be greater than total order items (${totalQty}).`;
    }

    return null;
  };

  // ------------------------------
  // Reset form & clear order data
  // ------------------------------
  const resetForm = () => {
    setOrder(null);
    setOrderProducts({});
    setMintsoftProductLookupComplete(false);
    setMintsoftProductLookupFailedIds([]);
    setSearchParams((prev) => ({ ...prev, orderNumber: '' }));
    setSelectedCourierService('');
    setNumberOfParcels('');
    setIsEditingShipment(false);
    setPrintWorkflow({
      active: false,
      stage: 'idle',
      message: '',
      severity: 'info',
      additionalDocCount: 0,
      canRetryAdditional: false,
      retryMode: null,
    });
    shipmentSnapshotRef.current = {
      selectedCourierService: '',
      numberOfParcels: '',
    };
    setKornitxBypassDialog({ open: false, status: '' });
    if (orderInputRef.current) {
      orderInputRef.current.focus();
    }
  };

  // ------------------------------
  // For Mintsoft orders: toggle shipment editing and allow cancel to restore saved values
  // ------------------------------
  const handleToggleEdit = () => {
    if (isEditingShipment) {
      setSelectedCourierService(shipmentSnapshotRef.current.selectedCourierService);
      setNumberOfParcels(shipmentSnapshotRef.current.numberOfParcels);
      setIsEditingShipment(false);
      return;
    }

    shipmentSnapshotRef.current = {
      selectedCourierService,
      numberOfParcels,
    };
    setIsEditingShipment(true);
  };

  const handleSaveShipment = () => {
    const validationMessage = validateNumberOfParcels(numberOfParcels, order, {
      fallbackToOrderValue: false,
    });
    if (validationMessage) {
      showNotification(validationMessage, 'warning');
      return;
    }

    shipmentSnapshotRef.current = {
      selectedCourierService,
      numberOfParcels,
    };

    const savedCourierService = normalizeShipmentValue(selectedCourierService);
    const savedParcelCount = normalizeShipmentValue(numberOfParcels);
    const originalCourierService = normalizeShipmentValue(order?.CourierServiceId);
    const originalParcelCount = normalizeShipmentValue(order?.NumberOfParcels);
    const matchesMintsoftValues =
      savedCourierService === originalCourierService &&
      savedParcelCount === originalParcelCount;

    if (matchesMintsoftValues) {
      removeShipmentDraft(order?.ID);
      setIsEditingShipment(false);
      showNotification('Shipment matches Mintsoft values. Local draft cleared.', 'info');
      return;
    }

    saveShipmentDraft(order?.ID, {
      selectedCourierService: savedCourierService,
      numberOfParcels: savedParcelCount,
    });
    setIsEditingShipment(false);
    showNotification('Shipment changes saved in this browser. Mintsoft will update only when you dispatch.', 'success');
  };

  // ------------------------------
  // Handle printing for Mintsoft orders
  // ------------------------------
  const getPdfBlobUrl = (base64Data) => {
    const byteCharacters = atob(base64Data);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i += 1) {
      byteArray[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    return window.URL.createObjectURL(blob);
  };

  const openPdfAndPrint = (base64Data, description = 'document') => {
    const docUrl = getPdfBlobUrl(base64Data);
    const printWindow = window.open(docUrl, '_blank');
    if (!printWindow) {
      window.URL.revokeObjectURL(docUrl);
      throw new Error(`Unable to open ${description}. Please allow popups for Shipping Station.`);
    }

    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.setTimeout(() => window.URL.revokeObjectURL(docUrl), 30000);
        resolve();
      };
      const fail = (error) => {
        if (settled) return;
        settled = true;
        window.URL.revokeObjectURL(docUrl);
        reject(error);
      };

      const fallbackTimer = window.setTimeout(finish, 15000);
      printWindow.addEventListener('load', () => {
        try {
          window.clearTimeout(fallbackTimer);
          printWindow.focus();
          printWindow.print();
          finish();
        } catch (error) {
          window.clearTimeout(fallbackTimer);
          fail(error);
        }
      }, { once: true });
    });
  };

  const printShippingLabel = async (shippingLabel) => {
    if (shippingLabel?.ZPLData && window.BrowserPrint) {
      return new Promise((resolve, reject) => {
        const fallbackLabelPrint = () => {
          if (!shippingLabel?.Base64Data) {
            reject(new Error('No printable shipping label PDF was returned by Mintsoft.'));
            return;
          }
          openPdfAndPrint(shippingLabel.Base64Data, 'shipping label')
            .then(resolve)
            .catch(reject);
        };

        window.BrowserPrint.getLocalDevices(
          'printer',
          (devices) => {
            const zebraPrinter = devices.find((d) => d.name === 'ZDesigner GK420d');
            if (zebraPrinter) {
              zebraPrinter.send(
                shippingLabel.ZPLData,
                resolve,
                (error) => {
                  console.error('Zebra printer error:', error);
                  fallbackLabelPrint();
                }
              );
            } else {
              fallbackLabelPrint();
            }
          },
          (error) => {
            console.error('BrowserPrint error:', error);
            fallbackLabelPrint();
          }
        );
      });
    }

    if (shippingLabel?.Base64Data && printers.length > 0 && selectedPrinter) {
      try {
        await axios.post('http://localhost:5237/print', {
          PrinterName: selectedPrinter,
          Content: shippingLabel.Base64Data,
          ContentType: 'application/pdf',
        });
        return;
      } catch (proxyError) {
        console.error('Local print proxy error:', proxyError);
      }
    }

    if (shippingLabel?.Base64Data) {
      await openPdfAndPrint(shippingLabel.Base64Data, 'shipping label');
      return;
    }

    throw new Error('No shipping label available to print.');
  };

  const printAdditionalDocuments = async (additionalDocs = []) => {
    const printableDocs = additionalDocs.filter((doc) => doc?.Base64Data);
    for (const doc of printableDocs) {
      await openPdfAndPrint(doc.Base64Data, doc.Name || 'Gift Note / additional document');
    }
    return printableDocs.length;
  };

  const handleAdditionalDocumentsPrint = async (orderForPrint = order, expectedCount = 0) => {
    if (!orderForPrint) {
      showNotification('Order not found for additional document printing', 'error');
      return false;
    }

    try {
      setPrintWorkflow({
        active: true,
        stage: 'additional-fetch',
        message: `Gift Note required - fetching ${expectedCount || 'additional'} document${expectedCount === 1 ? '' : 's'} from Mintsoft...`,
        severity: 'warning',
        additionalDocCount: expectedCount,
        canRetryAdditional: false,
        retryMode: null,
      });

      const response = await axios.get(`/api/linx-dispatch/documents/${orderForPrint.ID}`, {
        params: { mode: 'additional' },
        timeout: MINTSOFT_ORDER_TIMEOUT_MS,
      });
      const additionalDocs = response.data.additionalDocs || [];

      setPrintWorkflow({
        active: true,
        stage: 'additional-print',
        message: `Gift Note required - printing ${additionalDocs.length || expectedCount} document${(additionalDocs.length || expectedCount) === 1 ? '' : 's'}...`,
        severity: 'warning',
        additionalDocCount: additionalDocs.length || expectedCount,
        canRetryAdditional: false,
        retryMode: null,
      });

      const printedCount = await printAdditionalDocuments(additionalDocs);
      if (expectedCount > 0 && printedCount === 0) {
        throw new Error('Mintsoft returned additional document records but no printable PDF data.');
      }

      setPrintWorkflow({
        active: false,
        stage: 'complete',
        message: 'All documents printed - ready for next order.',
        severity: 'success',
        additionalDocCount: additionalDocs.length,
        canRetryAdditional: false,
        retryMode: null,
      });
      showNotification('Gift Note/additional documents printed. Ready for next order.', 'success');
      return true;
    } catch (error) {
      console.error('Error printing additional documents:', error);
      setPrintWorkflow({
        active: false,
        stage: 'failed',
        message: 'Gift Note/additional document did not print. Retry before moving to the next order.',
        severity: 'error',
        additionalDocCount: expectedCount,
        canRetryAdditional: true,
        retryMode: 'additional',
      });
      showNotification('Gift Note/additional document did not print. Please retry before continuing.', 'error');
      return false;
    }
  };

  const handlePrintDocuments = async (orderForPrint = order) => {
    if (!orderForPrint) {
      showNotification('Order not found for printing', 'error');
      return false;
    }

    try {
      setPrintWorkflow({
        active: true,
        stage: 'label-fetch',
        message: 'Fetching shipping label from Mintsoft...',
        severity: 'info',
        additionalDocCount: 0,
        canRetryAdditional: false,
        retryMode: null,
      });

      const response = await axios.get(`/api/linx-dispatch/documents/${orderForPrint.ID}`, {
        params: { mode: 'label' },
        timeout: MINTSOFT_ORDER_TIMEOUT_MS,
      });
      const { shippingLabel, additionalDocCount = 0 } = response.data;

      setPrintWorkflow({
        active: true,
        stage: 'label-print',
        message: additionalDocCount > 0
          ? `Printing shipping label first. Gift Note required (${additionalDocCount}) - please wait.`
          : 'Printing shipping label...',
        severity: additionalDocCount > 0 ? 'warning' : 'info',
        additionalDocCount,
        canRetryAdditional: false,
        retryMode: null,
      });

      await printShippingLabel(shippingLabel);
      showNotification(
        additionalDocCount > 0
          ? 'Shipping label printed. Gift Note is still required; please wait.'
          : 'Shipping label printed successfully.',
        additionalDocCount > 0 ? 'warning' : 'success'
      );

      if (additionalDocCount > 0) {
        return handleAdditionalDocumentsPrint(orderForPrint, additionalDocCount);
      }

      setPrintWorkflow({
        active: false,
        stage: 'complete',
        message: 'Shipping label printed - ready for next order.',
        severity: 'success',
        additionalDocCount: 0,
        canRetryAdditional: false,
        retryMode: null,
      });
      return true;
    } catch (error) {
      console.error('Error printing documents:', error);
      setPrintWorkflow({
        active: false,
        stage: 'failed',
        message: 'Shipping label did not print. Retry printing before moving to the next order.',
        severity: 'error',
        additionalDocCount: 0,
        canRetryAdditional: false,
        retryMode: 'label',
      });
      showNotification(error.message || 'Error fetching print documents', 'error');
      return false;
    }
  };

  const handleRetryPrintWorkflow = async () => {
    if (!order) return;
    setLoading(true);
    try {
      const printed = printWorkflow.retryMode === 'additional'
        ? await handleAdditionalDocumentsPrint(order, printWorkflow.additionalDocCount)
        : await handlePrintDocuments(order);
      if (printed) {
        window.setTimeout(resetForm, 700);
      }
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------
  // Handle search for Mintsoft orders
  // ------------------------------
  const handleMintsoftSearch = async () => {
    const lookupValue = searchParams.orderNumber.trim();
    if (!lookupValue) {
      showNotification('Please enter a valid value', 'warning');
      return;
    }
    if (searchParams.searchType === 'orderNumber' && !searchParams.selectedClientId) {
      showNotification('Please select a client', 'warning');
      return;
    }
    setLoading(true);
    try {
      let response;
      if (searchParams.searchType === 'orderNumber') {
        response = await axios.get('/api/linx-dispatch/search', {
          params: {
            orderNumber: lookupValue,
            clientId: searchParams.selectedClientId,
          },
          timeout: MINTSOFT_ORDER_TIMEOUT_MS,
        });
      } else {
        response = await axios.get(`/api/linx-dispatch/order/${encodeURIComponent(lookupValue)}`, {
          timeout: MINTSOFT_ORDER_TIMEOUT_MS,
        });
      }
      if (response.data.success) {
        const fetchedOrder = response.data.order;
        if (fetchedOrder.OrderStatusId === 4) {  // Dispatched status
          setConfirmDialog({
            open: true,
            orderToReprint: fetchedOrder.ID,
          });
          showNotification(`Order ${fetchedOrder.OrderNumber} is already dispatched. Do you want to reprint?`, 'info');
        } else {
          setOrder(fetchedOrder);
          const dispatchBlockedMessage = getMintsoftDispatchBlockedMessage(fetchedOrder);
          if (dispatchBlockedMessage) {
            showNotification(dispatchBlockedMessage, 'warning');
          }
        }
      } else {
        const errMsg = response.data.message;
        const dispatchedOrderId = String(response.data.orderId ?? '').trim();
        if (errMsg && errMsg.includes('DESPATCHED already') && isNumericOrderId(dispatchedOrderId)) {
          setConfirmDialog({
            open: true,
            orderToReprint: dispatchedOrderId,
          });
        } else if (errMsg && errMsg.includes('DESPATCHED already')) {
          showNotification('Order is already dispatched, but the Mintsoft order ID was missing. Please search again.', 'error');
        } else {
          showNotification(errMsg, 'error');
        }
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || error.message;
      showNotification(errMsg, 'error');
    }
    setLoading(false);
  };


  // ------------------------------
  // Handle search for KornitX orders
  // ------------------------------
  const handleKornitxSearch = async () => {
    if (!searchParams.orderNumber.trim()) {
      showNotification('Please enter a valid order number', 'warning');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `/api/linx-dispatch/kornitx-order/${encodeURIComponent(searchParams.orderNumber)}`,
        { params: { fallbackOrderId: searchParams.orderNumber } }  // Pass fallback ID here
      );
      if (response.data.success) {
        const fetchedOrder = response.data.order;
        const status = fetchedOrder.status_name;
        if (['Received', 'In Production'].includes(status)) {
          setOrder(fetchedOrder);
        } else {
          showNotification(
            `KornitX order cannot be dispatched. Current status: ${status}`,
            'warning'
          );
        }
      } else {
        showNotification(response.data.message, 'error');
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || error.message;
      showNotification(errMsg, 'error');
    }
    setLoading(false);
  };


  // ------------------------------
  // Handle dispatch for Mintsoft orders
  // ------------------------------
  const allItemsConfirmed = (() => {
    if (!order) return false;

    if (activeTab === 'dispatch' && order.OrderItems) {
      const allItemIds = order.OrderItems.map((item) => item.ID);
      return allItemIds.every((id) => confirmedLineIds.includes(id));
    }

    if (activeTab === 'kornitx' && order.items?.items) {
      const allItemIds = order.items.items.map((item) => item.id);
      return allItemIds.every((id) => confirmedLineIds.includes(id));
    }

    return false;
  })();
  const persistedShipmentDraft =
    activeTab === 'dispatch' && order?.ID ? getShipmentDraft(order.ID) : null;
  const originalCourierServiceId = normalizeShipmentValue(order?.CourierServiceId);
  const originalParcelCount = normalizeShipmentValue(order?.NumberOfParcels);
  const draftCourierServiceId = normalizeShipmentValue(persistedShipmentDraft?.selectedCourierService);
  const draftParcelCount = normalizeShipmentValue(persistedShipmentDraft?.numberOfParcels);
  const hasPersistedCourierOverride =
    Boolean(draftCourierServiceId) && draftCourierServiceId !== originalCourierServiceId;
  const hasPersistedParcelOverride =
    Boolean(draftParcelCount) && draftParcelCount !== originalParcelCount;
  const hasActiveShipmentDraft =
    Boolean(persistedShipmentDraft) && (hasPersistedCourierOverride || hasPersistedParcelOverride);
  const originalCourierServiceName =
    courierServices.find((service) => normalizeShipmentValue(service.ID) === originalCourierServiceId)?.Name
    || order?.CourierServiceName
    || 'N/A';
  const mintsoftLineCount =
    activeTab === 'dispatch' && order?.OrderItems ? order.OrderItems.length : 0;
  const mintsoftDisplayItemCount =
    activeTab === 'dispatch' ? mintsoftDisplayGroups.length : 0;
  const mintsoftConfirmedDisplayCount =
    activeTab === 'dispatch'
      ? mintsoftDisplayGroups.filter((group) => isMintsoftDisplayGroupConfirmed(group, confirmedLineIds)).length
      : 0;
  const mintsoftRemainingCount = Math.max(mintsoftDisplayItemCount - mintsoftConfirmedDisplayCount, 0);
  const mintsoftItemCountLabel = mintsoftDisplayItemCount !== mintsoftLineCount
    ? `${mintsoftDisplayItemCount} ${mintsoftDisplayItemCount === 1 ? 'item' : 'items'} (${mintsoftLineCount} lines)`
    : `${mintsoftLineCount} ${mintsoftLineCount === 1 ? 'line' : 'lines'}`;
  const isPrintWorkflowBlocking = printWorkflow.active || printWorkflow.stage === 'failed';

  // Dispatch order handler for Mintsoft orders
  const handleDispatchOrder = async ({ allowMissingKornitx = false } = {}) => {
    if (!order) return;

    if (isPrintWorkflowBlocking) {
      showNotification('Document printing is still pending for this order. Finish or retry it before continuing.', 'warning');
      return;
    }

    if (mintsoftDispatchInFlightRef.current) {
      showNotification('Dispatch already in progress. Mintsoft can take a few minutes; please wait and do not scan/click again.', 'info');
      return;
    }

    const activeDispatchLock = getActiveMintsoftDispatchLock(order.ID);
    if (activeDispatchLock) {
      const activeForSeconds = Math.max(1, Math.round((Date.now() - activeDispatchLock.startedAt) / 1000));
      showNotification(
        `Order ${activeDispatchLock.orderNumber || order.OrderNumber || order.ID} is already being dispatched in this browser (${activeForSeconds}s). Please wait for Mintsoft to finish.`,
        'info'
      );
      return;
    }

    if (isMintsoftProductLookupPending) {
      showNotification('Still checking product details before dispatch. Please wait a moment.', 'info');
      return;
    }

    const dispatchBlockedMessage = getMintsoftDispatchBlockedMessage(order);
    if (dispatchBlockedMessage) {
      showNotification(dispatchBlockedMessage, 'warning');
      return;
    }

    if (isEditingShipment) {
      showNotification('Please save or cancel the shipment changes before dispatching.', 'warning');
      return;
    }

    const dispatchLockToken = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    mintsoftDispatchInFlightRef.current = true;
    saveMintsoftDispatchLock(order.ID, {
      token: dispatchLockToken,
      startedAt: Date.now(),
      orderNumber: order.OrderNumber || order.ID,
    });
    setLoading(true);
    try {
      const parcelsValidationMessage = validateNumberOfParcels(numberOfParcels, order);
      if (parcelsValidationMessage) {
        showNotification(parcelsValidationMessage, 'warning');
        return;
      }
      // For orders with personalised items, check KornitX status.
      // Missing KornitX orders can still be dispatched in Mintsoft after an operator confirm.
      if (hasPersonalisedMintsoftItems) {
        const statusRes = await axios.get(`/api/linx-dispatch/kornitx-status/${order.ID}`);
        const refreshedStatus = statusRes.data.status_name || 'Unknown';
        setKornitxOrderStatus(refreshedStatus);
        if (refreshedStatus !== KORNITX_READY_STATUS) {
          if (isKornitxMissingStatus(refreshedStatus) && allowMissingKornitx) {
            showNotification(
              `KornitX order is ${refreshedStatus}. Dispatching in Mintsoft only.`,
              'info'
            );
          } else if (isKornitxMissingStatus(refreshedStatus)) {
            setKornitxBypassDialog({ open: true, status: refreshedStatus });
            return;
          } else {
            showNotification(`KornitX order is not In Production (Current: ${refreshedStatus})`, 'warning');
            return;
          }
        }
      }
      const originalCourierService = order.CourierServiceId || '';
      const originalNumberOfParcels = getOriginalNumberOfParcels(order);
      const courierIdToUse = selectedCourierService || originalCourierService;
      const parcelsToUse = getEffectiveNumberOfParcels(numberOfParcels, order);
      const hasParcelOverride = parcelsToUse !== originalNumberOfParcels;
      const dispatchParams = new URLSearchParams({
        orderNumber: order.OrderNumber || '',
        CourierServiceId: courierIdToUse,
      });
      if (hasParcelOverride && parcelsToUse) {
        dispatchParams.set('NumberOfParcels', parcelsToUse);
      }
      if (canTrustMintsoftPersonalisationHint) {
        dispatchParams.set('HasPersonalisedItems', hasPersonalisedMintsoftItems ? 'true' : 'false');
      }
      const url = `/api/linx-dispatch/dispatch/${order.ID}?${dispatchParams.toString()}`;
      showNotification('Dispatch sent to Mintsoft. Please wait for the label; this can take a few minutes when Mintsoft is slow.', 'info');
      const response = await axios.post(url, null, { timeout: MINTSOFT_DISPATCH_REQUEST_TIMEOUT_MS });
      if (response.data.success) {
        removeShipmentDraft(order.ID);
        showNotification(response.data.message, 'success');
        const printed = await handlePrintDocuments(order);
        if (printed) {
          window.setTimeout(resetForm, 700);
        }
      } else {
        showNotification(response.data.message, 'error');
      }
    } catch (error) {
      console.error('Dispatch error:', error);
      const status = error.response?.status;
      const upstreamStatus = error.response?.data?.upstreamStatus;
      const message = error.response?.data?.message || error.message;
      if (status === 409) {
        showNotification(message || 'Dispatch already in progress. Please wait for the current request to finish.', 'info');
      } else if (
        ['ECONNABORTED', 'ETIMEDOUT'].includes(error.code)
        || [429, 502, 503, 504].includes(status)
        || [429, 502, 503, 504].includes(upstreamStatus)
      ) {
        showNotification(message || 'Mintsoft is responding slowly or unavailable. Please retry shortly.', 'warning');
      } else {
        showNotification(message, 'error');
      }
    } finally {
      removeMintsoftDispatchLock(order.ID, dispatchLockToken);
      mintsoftDispatchInFlightRef.current = false;
      setLoading(false);
    }
  };

  // ------------------------------
  // Handle dispatch for KornitX orders only
  // ------------------------------
  // New helper: Clean a base64 string by removing any data URL prefix and whitespace/newlines
  function cleanBase64(b64Data) {
    // Remove data URL prefix if present
    if (b64Data.startsWith('data:')) {
      b64Data = b64Data.split(',')[1];
    }
    // Remove all non-base64 characters including line breaks, zero-width spaces, etc.
    return b64Data.replace(/[^A-Za-z0-9+/=]/g, '');
  }

  // Updated helper: Convert Base64 to Blob using the cleaned string
  function b64toBlob(b64Data, contentType) {
    const cleanedB64 = cleanBase64(b64Data);
    //console.log('PDF base64 snippet:', b64Data.slice(0, 30));
    const byteCharacters = atob(cleanedB64);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  // Updated helper: Handle Gift Message PDF printing for KornitX orders
  const handleGiftMessagePrint = async (giftMessage, selectedPrinter) => {
    try {
      const res = await axios.post('/api/linx-dispatch/gift-message-to-pdf', {
        giftMessage,
        width: giftWidth,
        height: giftHeight,
      });

      if (res.data && res.data.pdfBase64) {
        const blob = b64toBlob(res.data.pdfBase64, 'application/pdf');
        //console.log('PDF Blob size:', blob.size);

        //Fallback to prompt print windows for manual printing
        /*if (!selectedPrinter || !printers.includes(selectedPrinter)) {
          // No valid printer, fallback to open in browser
          const pdfUrl = URL.createObjectURL(blob);
          const printWindow = window.open(pdfUrl, '_blank');
          printWindow.addEventListener('load', () => {
            printWindow.print();
          });
          showNotification('No printer selected. Opened PDF in new tab for manual print.', 'info');
          return;
        }*/

        //Sending PDF FunkyPigeon Gift Message to Printer
        //console.log('Sending to printer:', selectedPrinter);
        /*await axios.post('http://localhost:5237/print', {
          PrinterName: selectedPrinter,
          Content: res.data.pdfBase64,
          ContentType: 'application/pdf',
        });
  
        */

        const pdfUrl = URL.createObjectURL(blob);
        const printWindow = window.open(pdfUrl, '_blank');
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
        //showNotification('No printer selected. Opened PDF in new tab for manual print.', 'info');
        showNotification('Gift message printed successfully.', 'success');
      } else {
        showNotification('Gift message PDF generation failed.', 'error');
      }
    } catch (error) {
      console.error('Error printing gift message:', error);
      showNotification('Error printing gift message.', 'error');
    }
  };


  // Modify the KornitX dispatch function to include GiftMessage handling
  const handleKornitxDispatch = async () => {
    setLoading(true);
    try {
      // Build payload with shipping details if provided.
      const dispatchPayload = {
        kornitxOrderId: order.id,
        externalRef: order.external_ref,
        companyRefId: order.secondary_company_ref_id,
        updateStatusOnly: false,
      };
      if (kxShippingCarrier) {
        dispatchPayload.shipping_carrier = kxShippingCarrier;
      }
      if (kxShippingMethod) {
        dispatchPayload.shipping_method = kxShippingMethod;
      }

      const dispatchRes = await axios.post('/api/linx-dispatch/kornitx-dispatch-only', dispatchPayload);

      if (dispatchRes.data.success) {
        showNotification('KornitX order dispatched successfully.', 'success');
        await handleGiftMessagePrint(order.GiftMessage, selectedPrinter);
        resetForm();
      } else {
        showNotification(dispatchRes.data.message, 'error');
      }
    } catch (error) {
      showNotification(error.response?.data?.message || error.message, 'error');
    }
    setLoading(false);
  };

  // ------------------------------
  // Handle reprint for Mintsoft orders
  // ------------------------------
  const handleReprint = async () => {
    const lookupValue = searchParams.orderNumber.trim();
    if (!lookupValue) {
      showNotification('Please enter a valid value', 'warning');
      return;
    }
    if (searchParams.searchType === 'orderNumber' && !searchParams.selectedClientId) {
      showNotification('Please select a client', 'warning');
      return;
    }

    setLoading(true);
    try {
      if (searchParams.searchType === 'orderId') {
        const response = await axios.get(
          `/api/linx-dispatch/order/${encodeURIComponent(lookupValue)}?reprint=true`,
          { timeout: MINTSOFT_ORDER_TIMEOUT_MS }
        );
        if (response.data.success) {
          const printed = await handlePrintDocuments(response.data.order);
          if (printed) {
            showNotification('Reprint initiated. Check your print dialog.', 'success');
            resetForm();
          }
        } else {
          showNotification(response.data.message, 'error');
        }
      } else {
        const searchResponse = await axios.get('/api/linx-dispatch/search', {
          params: {
            orderNumber: lookupValue,
            clientId: searchParams.selectedClientId,
          },
          timeout: MINTSOFT_ORDER_TIMEOUT_MS,
        });

        if (searchResponse.data.success) {
          const printed = await handlePrintDocuments(searchResponse.data.order);
          if (printed) {
            showNotification('Reprint initiated. Check your print dialog.', 'success');
            resetForm();
          }
        } else if (
          searchResponse.data.message?.includes('DESPATCHED already') &&
          searchResponse.data.orderId
        ) {
          const reprintResponse = await axios.get(
            `/api/linx-dispatch/order/${encodeURIComponent(searchResponse.data.orderId)}?reprint=true`,
            { timeout: MINTSOFT_ORDER_TIMEOUT_MS }
          );
          if (reprintResponse.data.success) {
            const printed = await handlePrintDocuments(reprintResponse.data.order);
            if (printed) {
              showNotification('Reprint initiated. Check your print dialog.', 'success');
              resetForm();
            }
          } else {
            showNotification(reprintResponse.data.message, 'error');
          }
        } else {
          showNotification(searchResponse.data.message, 'error');
        }
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || error.message;
      showNotification(errMsg, 'error');
    }
    setLoading(false);
  };

  const handleDialogConfirm = async () => {
    try {
      const targetOrderId = String(confirmDialog.orderToReprint ?? '').trim();
      if (!isNumericOrderId(targetOrderId)) {
        showNotification('Unable to reprint because the Mintsoft order ID is missing. Please search again.', 'error');
      } else {
        const reprintResponse = await axios.get(
          `/api/linx-dispatch/order/${encodeURIComponent(targetOrderId)}?reprint=true`,
          { timeout: MINTSOFT_ORDER_TIMEOUT_MS }
        );
        if (reprintResponse.data.success) {
          const printed = await handlePrintDocuments(reprintResponse.data.order);
          if (printed) {
            resetForm();
          }
        }
      }
    } catch (error) {
      showNotification(error.response?.data?.message || error.message, 'error');
    }
    setConfirmDialog({ open: false, orderToReprint: null });
  };

  const handleDialogClose = () => {
    setConfirmDialog({ open: false, orderToReprint: null });
    resetForm();
  };

  // ------------------------------
  // Re-fetch Mintsoft product details if order changes (optional)
  // ------------------------------
  useEffect(() => {
    if (order && activeTab === 'dispatch' && order.OrderItems) {
      const fetchProducts = async () => {
        const productMap = {};
        await Promise.all(
          order.OrderItems.map(async (item) => {
            try {
              const response = await axios.get(`/api/linx-dispatch/product/${item.ProductId}`, {
                timeout: MINTSOFT_PRODUCT_LOOKUP_TIMEOUT_MS,
              });
              productMap[item.ProductId] = response.data.product;
            } catch (err) {
              console.error(`Error fetching product ${item.ProductId}:`, err.message);
            }
          })
        );
        setOrderProducts(productMap);
      };
      fetchProducts();
    }
  }, [order, activeTab]);

  // ------------------------------
  // Focus "Add All Items" button when order loads
  // ------------------------------
  useEffect(() => {
    if (order && addAllButtonRef.current) {
      addAllButtonRef.current.focus();
    }
  }, [order]);

  // ------------------------------
  // For Mintsoft orders: if all non-personalised items are confirmed, focus dispatch button
  // ------------------------------
  useEffect(() => {
    if (order && activeTab === 'dispatch' && order.OrderItems) {
      const nonPersonalisedIds = order.OrderItems
        .filter((item) => !isPersonalised(item.ID))
        .map((item) => item.ID);
      const allConfirmed = nonPersonalisedIds.every((id) => confirmedLineIds.includes(id));
      if (allConfirmed && !isEditingShipment && dispatchButtonRef.current) {
        dispatchButtonRef.current.focus();
        dispatchButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [confirmedLineIds, order, activeTab, isEditingShipment]);

  // ------------------------------
  // For KornitX orders: if all items (from order.items.items) are confirmed, focus dispatch button
  // ------------------------------
  useEffect(() => {
    if (order && activeTab === 'kornitx' && order.items && order.items.items) {
      const allItemIds = order.items.items.map((item) => item.id);
      const allConfirmed = allItemIds.every((id) => confirmedLineIds.includes(id));
      if (allConfirmed && dispatchButtonRef.current) {
        dispatchButtonRef.current.focus();
        dispatchButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [confirmedLineIds, order, activeTab]);

  // ------------------------------
  // Global listen to F2 keyboard shortcut for quickly adding all items and dispatching
  // ------------------------------
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F2') {
        if (e.repeat) return;
        e.preventDefault();

        if (!order) return;

        if (activeTab === 'dispatch' && isEditingShipment) {
          showNotification('Please save or cancel the shipment changes before using F2 to dispatch.', 'warning');
          return;
        }

        if (activeTab === 'dispatch' && isPrintWorkflowBlocking) {
          showNotification('Document printing is still pending for this order. Finish or retry it before continuing.', 'warning');
          return;
        }

        if (activeTab === 'dispatch' && order.OrderItems?.length > 0) {
          if (mintsoftDispatchInFlightRef.current) {
            showNotification('Dispatch already in progress. Please wait for the current request to finish.', 'info');
            return;
          }

          if (isMintsoftProductLookupPending) {
            showNotification('Still checking product details before dispatch. Please wait a moment.', 'info');
            return;
          }

          const dispatchBlockedMessage = getMintsoftDispatchBlockedMessage(order);
          if (dispatchBlockedMessage) {
            showNotification(dispatchBlockedMessage, 'warning');
            return;
          }

          const allItemIds = order.OrderItems.map((item) => item.ID);
          setConfirmedLineIds(allItemIds);

          showNotification('All items confirmed and dispatch initiated with F2 shortcut!', 'info');
          handleDispatchOrder();
        }

        if (activeTab === 'kornitx' && order.items?.items?.length > 0) {
          const allItemIds = order.items.items.map((item) => item.id);
          setConfirmedLineIds(allItemIds);

          showNotification('All items confirmed and dispatch initiated with F2 shortcut!', 'info');

          setTimeout(() => {
            handleKornitxDispatch();
          }, 100);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [order, activeTab, isEditingShipment, isMintsoftProductLookupPending, isPrintWorkflowBlocking]);

  useEffect(() => {
    if (order && !sessionStorage.getItem('f2TipShown')) {
      showNotification('💡 Tip: Press F2 to confirm all items and dispatch instantly.', 'info');
      sessionStorage.setItem('f2TipShown', 'true');
    }
  }, [order]);


  // ------------------------------
  // Render JSX
  // ------------------------------
  return (
    <Layout>
      <Box sx={{ width: '100%', typography: 'body1' }}>
        <Paper
          sx={{
            p: { xs: 1, md: 1.15 },
            mb: 1.25,
            borderRadius: 3,
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.065)',
            display: 'flex',
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 1,
            flexDirection: { xs: 'column', md: 'row' },
            width: '100%',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 900, letterSpacing: 0.7, lineHeight: 1, textTransform: 'uppercase' }}>
              Dispatch Console
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.15, color: 'text.primary' }}>
              Shipping Station
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Scan, dispatch, and reprint labels from one operator screen.
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={activeTab}
            exclusive
            onChange={(e, newVal) => {
              if (isPrintWorkflowBlocking) {
                showNotification('Finish or retry the required document printing before changing station mode.', 'warning');
                return;
              }
              if (newVal) {
                setActiveTab(newVal);
                resetForm();
              }
            }}
            size="small"
            sx={{
              alignSelf: { xs: 'stretch', md: 'center' },
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, auto)' },
              gap: 0.5,
              p: 0.35,
              borderRadius: 2,
              border: '1px solid',
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
              backgroundColor: '#f3f7fb',
              '& .MuiToggleButtonGroup-grouped': {
                border: 0,
                borderRadius: '10px !important',
                mx: 0,
              },
              '& .MuiToggleButton-root': {
                px: 1,
                py: 0.55,
                minHeight: 34,
                textTransform: 'none',
                fontWeight: 800,
                fontSize: 12.5,
                color: 'text.secondary',
                bgcolor: 'transparent',
                '&.Mui-selected': {
                  bgcolor: '#fff',
                  color: 'primary.main',
                  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
                  '&:hover': {
                    bgcolor: '#fff',
                  },
                },
                '&:hover': {
                  bgcolor: '#fff',
                },
              },
            }}
          >
            <ToggleButton value="dispatch" disabled={isPrintWorkflowBlocking}>
              <LocalShippingIcon sx={{ mr: 0.75, fontSize: 18 }} /> Dispatch
            </ToggleButton>
            <ToggleButton value="reprint" disabled={isPrintWorkflowBlocking}>
              <PrintIcon sx={{ mr: 0.75, fontSize: 18 }} /> Reprint
            </ToggleButton>
            <ToggleButton value="kornitx" disabled={isPrintWorkflowBlocking}>
              <LocalShippingIcon sx={{ mr: 0.75, fontSize: 18 }} /> KornitX
            </ToggleButton>
            <ToggleButton value="kornitx-slip" disabled={isPrintWorkflowBlocking}>
              <PrintIcon sx={{ mr: 0.75, fontSize: 18 }} /> Packing Slip
            </ToggleButton>
          </ToggleButtonGroup>
        </Paper>

        {/* Render Search Form based on activeTab */}
        {activeTab === 'dispatch' && !order && (
          <SearchForm
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            printers={printers}
            printerProxyUnavailable={printerProxyUnavailable}
            selectedPrinter={selectedPrinter}
            setSelectedPrinter={setSelectedPrinter}
            onSearch={handleMintsoftSearch}
            loading={loading}
            inputRef={orderInputRef}
            isDispatch
            orderSource="mintsoft"
          />
        )}
        {activeTab === 'kornitx' && !order && (
          <SearchForm
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            printers={printers}
            printerProxyUnavailable={printerProxyUnavailable}
            selectedPrinter={selectedPrinter}
            setSelectedPrinter={setSelectedPrinter}
            onSearch={handleKornitxSearch}
            loading={loading}
            inputRef={orderInputRef}
            isDispatch
            orderSource="kornitx"
          />
        )}
        {activeTab === 'kornitx-slip' && <PrintPackingSlip />}
        {activeTab === 'reprint' && !order && (
          <SearchForm
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            printers={printers}
            printerProxyUnavailable={printerProxyUnavailable}
            selectedPrinter={selectedPrinter}
            setSelectedPrinter={setSelectedPrinter}
            onSearch={handleReprint}
            loading={loading}
            inputRef={orderInputRef}
            isDispatch={false}
            orderSource="mintsoft"
          />
        )}

        {/* Mintsoft Dispatch Order */}
        {activeTab === 'dispatch' && order && (
          <Paper
            sx={{
              p: { xs: 1.25, md: 1.75 },
              boxShadow: '0 14px 34px rgba(15, 23, 42, 0.08)',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              height: { xs: 'auto', md: 'calc(100vh - 188px)' },
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="outlined" onClick={resetForm} disabled={isPrintWorkflowBlocking || loading}>
                  Back
                </Button>
                <Chip
                  label={mintsoftItemCountLabel}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`${mintsoftConfirmedDisplayCount}/${mintsoftDisplayItemCount} confirmed`}
                  size="small"
                  color={allItemsConfirmed ? 'success' : 'default'}
                  variant={allItemsConfirmed ? 'filled' : 'outlined'}
                />
                <Chip
                  label="F2 quick dispatch"
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
                {hasActiveShipmentDraft && (
                  <Chip
                    label="Local shipment draft"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Stack>
              <Button
                ref={dispatchButtonRef}
                variant="contained"
                color="secondary"
                onClick={() => handleDispatchOrder()}
                size="large"
                disabled={
                  !allItemsConfirmed
                  || loading
                  || isPrintWorkflowBlocking
                  || isMintsoftProductLookupPending
                  || isEditingShipment
                  || Boolean(getMintsoftDispatchBlockedMessage(order))
                }
                sx={{ minWidth: { xs: '100%', sm: 180 }, fontWeight: 700 }}
              >
                {loading ? <CircularProgress size={24} /> : (printWorkflow.stage === 'failed' ? 'Resolve Print Issue' : (printWorkflow.active ? 'Printing Documents...' : (isMintsoftProductLookupPending ? 'Checking Products...' : 'Dispatch Order')))}
              </Button>
            </Box>

            {isKornitxMissingStatus(kornitxOrderStatus) && (
              <Alert severity="warning" variant="outlined" sx={{ '& .MuiAlert-message': { fontWeight: 700 } }}>
                This personalised order is not in KornitX. You can still dispatch it in Mintsoft after confirming the items.
              </Alert>
            )}

            {printWorkflow.message && (
              <Alert
                severity={printWorkflow.severity}
                variant="filled"
                action={
                  printWorkflow.stage === 'failed' ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleRetryPrintWorkflow}
                      disabled={loading}
                      sx={{ fontWeight: 900 }}
                    >
                      Retry
                    </Button>
                  ) : null
                }
                sx={{
                  alignItems: 'center',
                  '& .MuiAlert-message': { fontWeight: 800 },
                }}
              >
                {printWorkflow.message}
              </Alert>
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(320px, 380px) minmax(0, 1fr)' },
                gap: 1.5,
                flexGrow: 1,
                minHeight: 0,
              }}
            >
              <OrderDetails
                order={order}
                courierServices={courierServices}
                selectedCourierService={selectedCourierService}
                setSelectedCourierService={setSelectedCourierService}
                numberOfParcels={numberOfParcels}
                setNumberOfParcels={setNumberOfParcels}
                isEditingShipment={isEditingShipment}
                onToggleEdit={handleToggleEdit}
                onSaveShipment={handleSaveShipment}
                checkingStatus={checkingStatus}
                kornitxOrderStatus={kornitxOrderStatus}
                fetchKornitxStatus={fetchKornitxStatus}
                orderSource="mintsoft"
                shipmentDraftIndicator={{
                  isActive: hasActiveShipmentDraft,
                  changedCourier: hasPersistedCourierOverride,
                  changedParcels: hasPersistedParcelOverride,
                  originalCourierServiceName,
                  originalParcelCount: originalParcelCount || 'N/A',
                }}
              />

              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  overflow: 'hidden',
                  boxShadow: 'none',
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                  borderBottom: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(180deg, #fafcff 0%, #ffffff 100%)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 1.5,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      Dispatch Checklist
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Check the images, then press F2 or dispatch when ready.
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={allItemsConfirmed ? 'Ready to dispatch' : `${mintsoftRemainingCount} left to confirm`}
                      size="small"
                      color={allItemsConfirmed ? 'success' : 'default'}
                      variant={allItemsConfirmed ? 'filled' : 'outlined'}
                    />
                    <Button
                      ref={addAllButtonRef}
                      variant="contained"
                      color="secondary"
                      size="small"
                      title="Shortcut: F2 to confirm all items and dispatch"
                      disabled={isPrintWorkflowBlocking || loading}
                      onClick={() => {
                        const allItemIds = order.OrderItems.map((item) => item.ID);
                        setConfirmedLineIds((prev) =>
                          Array.from(new Set([...prev, ...allItemIds]))
                        );
                      }}
                    >
                      Confirm All Items
                    </Button>
                  </Stack>
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    overflowY: 'auto',
                    flexGrow: 1,
                    minHeight: 0,
                  }}
                  tabIndex={0}
                >
                  <OrderItems
                    order={order}
                    orderProducts={orderProducts}
                    bundleDetails={bundleDetails}
                    lineImagesMap={lineImagesMap}
                    confirmedLineIds={confirmedLineIds}
                    setConfirmedLineIds={setConfirmedLineIds}
                    isPersonalised={isPersonalised}
                    orderSource="mintsoft"
                  />
                </Box>
              </Paper>
            </Box>
          </Paper>
        )}

        {/* KornitX Dispatch Order */}
        {activeTab === 'kornitx' && order && (
          <Paper
            sx={{
              p: 3,
              boxShadow: 4,
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              minHeight: '400px',
            }}
          >
            <Box
              sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}
            >
              <Button variant="outlined" onClick={resetForm}>
                Back
              </Button>

              {/* Gift Message Paper Size Config remains unchanged */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ fontWeight: 'bold' }}>Gift Msg Size:</Box>
                <select
                  value={selectedSize}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedSize(val);
                    if (presetSizes[val]) {
                      setGiftWidth(presetSizes[val].width);
                      setGiftHeight(presetSizes[val].height);
                    }
                  }}
                  style={{ height: 32 }}
                >
                  {Object.keys(presetSizes).map((label) => (
                    <option key={label} value={label}>{label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={giftWidth}
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    setGiftWidth(value.endsWith('mm') ? value : `${parseFloat(value)}mm`);
                    setSelectedSize('Custom');
                  }}
                  placeholder="Width"
                  style={{ width: 70 }}
                />
                <input
                  type="text"
                  value={giftHeight}
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    setGiftHeight(value.endsWith('mm') ? value : `${parseFloat(value)}mm`);
                    setSelectedSize('Custom');
                  }}
                  placeholder="Height"
                  style={{ width: 70 }}
                />
              </Box>

              <Button
                ref={dispatchButtonRef}
                variant="contained"
                color="secondary"
                onClick={handleKornitxDispatch}
                size="large"
                disabled={!allItemsConfirmed || loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Dispatch KornitX Order'}
              </Button>
            </Box>

            {/* Pass extra props for shipping details to OrderDetails */}
            <OrderDetails
              order={order}
              courierServices={[]} // not used for KornitX orders
              selectedCourierService={''}
              setSelectedCourierService={() => { }}
              numberOfParcels={''}
              setNumberOfParcels={() => { }}
              isEditingShipment={isEditingKXShipping}
              onToggleEdit={() => setIsEditingKXShipping(!isEditingKXShipping)}
              checkingStatus={checkingStatus}
              kornitxOrderStatus={kornitxOrderStatus}
              fetchKornitxStatus={fetchKornitxStatus}
              orderSource="kornitx"
              kxShippingCarrier={kxShippingCarrier}
              setKxShippingCarrier={setKxShippingCarrier}
              kxShippingMethod={kxShippingMethod}
              setKxShippingMethod={setKxShippingMethod}
            />

            {/* OrderItems for KornitX remain unchanged */}
            <Box
              sx={{
                overflowY: 'auto',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                position: 'relative',
              }}
              tabIndex={0}
            >
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  backgroundColor: 'background.paper',
                  p: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    ref={addAllButtonRef}
                    variant="contained"
                    color="secondary"
                    title="Shortcut: F2 to confirm all items and dispatch"
                    onClick={() => {
                      const allItemIds = order.items?.items?.map((item) => item.id) || [];
                      setConfirmedLineIds(allItemIds);
                    }}
                  >
                    Add All Items (↵ Enter)
                  </Button>

                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Tip: Press <strong>F2</strong> to confirm and dispatch instantly
                  </Typography>
                </Box>
              </Box>
              <OrderItems
                order={order}
                orderProducts={{}} // not used for KornitX
                lineImagesMap={{}} // artwork is in the item itself
                confirmedLineIds={confirmedLineIds}
                setConfirmedLineIds={setConfirmedLineIds}
                orderSource="kornitx"
              />
            </Box>
          </Paper>
        )}

        {/* Reprint (Mintsoft) */}
        {activeTab === 'reprint' && order && (
          <Paper
            sx={{
              p: 3,
              boxShadow: 4,
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              minHeight: '400px',
            }}
          >
            <OrderDetails
              order={order}
              courierServices={courierServices}
              selectedCourierService={selectedCourierService}
              setSelectedCourierService={setSelectedCourierService}
              numberOfParcels={numberOfParcels}
              setNumberOfParcels={setNumberOfParcels}
              isEditingShipment={isEditingShipment}
              onToggleEdit={handleToggleEdit}
              checkingStatus={checkingStatus}
              kornitxOrderStatus={kornitxOrderStatus}
              fetchKornitxStatus={fetchKornitxStatus}
              orderSource="mintsoft"
            />
          </Paper>
        )}

        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleNotificationClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleNotificationClose} severity={notification.severity} sx={{ width: '100%' }}>
            {notification.message}
          </Alert>
        </Snackbar>

        <ConfirmationDialog
          open={confirmDialog.open}
          orderToReprint={confirmDialog.orderToReprint}
          onClose={handleDialogClose}
          onConfirm={handleDialogConfirm}
        />

        <Dialog
          open={kornitxBypassDialog.open}
          onClose={() => setKornitxBypassDialog({ open: false, status: '' })}
        >
          <DialogTitle>Dispatch without KornitX?</DialogTitle>
          <DialogContent>
            <Typography>
              This personalised order is not in KornitX
              {kornitxBypassDialog.status ? ` (current status: ${kornitxBypassDialog.status})` : ''}.
              Dispatch it in Mintsoft anyway?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setKornitxBypassDialog({ open: false, status: '' })}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setKornitxBypassDialog({ open: false, status: '' });
                handleDispatchOrder({ allowMissingKornitx: true });
              }}
            >
              Dispatch in Mintsoft
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default LinxDispatch;
